package receipt

abstract class Flag(val bitValue: Int)

interface FlagExtraction {
    val allFlags: List<Flag>

    fun flagsFor(value: Int?): List<String> {
        if (value == null) return emptyList()
        return allFlags.filter { value and it.bitValue != 0 }.map { it::class.simpleName!! }
    }
}

sealed class OrderStatuses(val orderStatus: Int): Flag(orderStatus) {
    data object Open: OrderStatuses(1)
    data object Canceled: OrderStatuses(2)
    data object Completed: OrderStatuses(4)
    data object Voided: OrderStatuses(8)
}

sealed class PaymentStatusTypes(val paymentStatusType: Int): Flag(paymentStatusType) {
    data object Canceled: PaymentStatusTypes(1)
    data object CardPresented: PaymentStatusTypes(2)
    data object PaymentRequested: PaymentStatusTypes(4)
    data object PresentedEnrollment: PaymentStatusTypes(8)
    data object WaitingEnrollmentDecision: PaymentStatusTypes(16)
    data object AcceptedEnrollment: PaymentStatusTypes(32)
    data object DeclinedEnrollment: PaymentStatusTypes(64)
    data object AlreadyMember: PaymentStatusTypes(128)
    data object PresentedSelectOffer: PaymentStatusTypes(256)
    data object CompletedSelectOffer: PaymentStatusTypes(512)
    data object PresentedRecap: PaymentStatusTypes(1024)
    data object RequestedAuthorization: PaymentStatusTypes(2048)
    data object CompletedAuthorization: PaymentStatusTypes(4096)
    data object Complete: PaymentStatusTypes(8192)
    data object ApplyFailedToPOS: PaymentStatusTypes(16384)
    data object TokenInPaymentDecision: PaymentStatusTypes(32768)
    data object PaymentAdjustmentRequested: PaymentStatusTypes(65536)
    data object ApplyingDiscount: PaymentStatusTypes(131072)
    data object Batched: PaymentStatusTypes(262144)
    data object VoidPending: PaymentStatusTypes(524288)
    data object CancelPending: PaymentStatusTypes(1048576)
    data object Voided: PaymentStatusTypes(2097152)
    data object OpenTab: PaymentStatusTypes(4194304)
}

sealed class PaymentTypes(val paymentTypeId: Int): Flag(paymentTypeId) {
    data object Credit: PaymentTypes(1)
    data object Cash: PaymentTypes(2)
    data object Chip: PaymentTypes(4)
    data object Swipe: PaymentTypes(8)
    data object Contactless: PaymentTypes(16)
    data object ChargeAccount: PaymentTypes(32)
    data object Debit: PaymentTypes(64)
}

sealed class QuestionTypes(val questionType: Int): Flag(questionType) {
    data object Mandatory: QuestionTypes(1)
    data object Important: QuestionTypes(2)
    data object FiveStar: QuestionTypes(4)
    data object Thumb: QuestionTypes(8)
}

sealed class OptInTypes(val optInType: Int): Flag(optInType) {
    data object OfferSMS: OptInTypes(1)
    data object OfferEmail: OptInTypes(2)
    data object ReceiptSMS: OptInTypes(4)
    data object ReceiptEmail: OptInTypes(8)
    data object TermsAndConditionsNotAccepted: OptInTypes(16)
    data object OptedOut: OptInTypes(32)
    data object WeeklySMS: OptInTypes(64)
    data object WeeklyEmail: OptInTypes(128)
}

sealed class OfferStatusTypes(val offerStatusType: Int) {
    data object PendingApproval: OfferStatusTypes(1)
    data object Declined: OfferStatusTypes(2)
    data object Pending: OfferStatusTypes(3)
    data object Active: OfferStatusTypes(4)
    data object PrimaryEarn: OfferStatusTypes(5)
    data object PrimaryEnrollment: OfferStatusTypes(6)
    data object PrimarySpend: OfferStatusTypes(7)
    data object Suspended: OfferStatusTypes(8)
    data object Deleted: OfferStatusTypes(9)
    data object PendingNoText : OfferStatusTypes(10)
    companion object {
        fun fromInt(type: Int): OfferStatusTypes {
            return when(type) {
                1 -> PendingApproval
                2 -> Declined
                3 -> Pending
                4 -> Active
                5 -> PrimaryEarn
                6 -> PrimaryEnrollment
                7 -> PrimarySpend
                8 -> Suspended
                9 -> Deleted
                10 -> PendingNoText
                else -> throw Exception("Invalid Type $type")
            }
        }
    }
}

sealed class LoyaltyProgramStatusTypes(val loyaltyProgramStatusType: Int) {
    data object Active: LoyaltyProgramStatusTypes(1)
    data object Suspended: LoyaltyProgramStatusTypes(2)
    data object Incomplete: LoyaltyProgramStatusTypes(3)
    data object PendingApproval: LoyaltyProgramStatusTypes(4)
    companion object {
        fun fromInt(type: Int): LoyaltyProgramStatusTypes {
            return when(type) {
                1 -> Active
                2 -> Suspended
                3 -> Incomplete
                4 -> PendingApproval
                else -> throw Exception("Invalid Type $type")
            }
        }
    }
}

sealed class OfferReceiveTypes(val offerReceiveTypes: Int): Flag(offerReceiveTypes) {
    data object ProductValuedUpTo: OfferReceiveTypes(1)
    data object Reward: OfferReceiveTypes(2)
    data object Point: OfferReceiveTypes(4)
    data object OrderSpecific: OfferReceiveTypes(8)
    data object ProductSpecific: OfferReceiveTypes(16)
    data object ReceiveFixedAmount: OfferReceiveTypes(32)
    data object ReceivePercentage: OfferReceiveTypes(64)
    data object AutoApply: OfferReceiveTypes(128)
    data object Instant: OfferReceiveTypes(256)
    data object Discount: OfferReceiveTypes(512)
    data object Bitcoin: OfferReceiveTypes(1024)
}

sealed class DeviceStatuses(val deviceStatus: Int): Flag(deviceStatus) {
    data object Hidden: DeviceStatuses(16)
    data object Registered: DeviceStatuses(32)
}

sealed class OriginType(val originType: Int): Flag(originType) {
    data object Terminal: OriginType(1)
    data object SDK: OriginType(2)
    data object SDK_API: OriginType(4)
    data object Dejavoo: OriginType(8)
    data object Order_Counter: OriginType(16)
}

sealed class ClientAccessStatuses(val clientAccessStatus: Int) {
    data object Active: ClientAccessStatuses(1)
    data object Suspended: ClientAccessStatuses(2)
}

sealed class MerchantStatusTypes(val merchantStatusType: Int) {
    data object Active: MerchantStatusTypes(1)
    data object FreeTier: MerchantStatusTypes(3)
}

sealed class MerchantAccessStatusTypes(val merchantAccessStatusType: Int) {
    data object Active: MerchantAccessStatusTypes(1)
}

sealed class TeamStatusTypes(val teamStatusType: Int) {
    data object Active: TeamStatusTypes(1)
}

sealed class TeamRepresentativeStatusTypes(val teamRepresentativeStatusType: Int) {
    data object Active: TeamRepresentativeStatusTypes(1)
}

sealed class RepresentativeAccessTypes(val representativeAccessType: Int): Flag(representativeAccessType) {
    data object MerchantAdmin: RepresentativeAccessTypes(1)
    data object LoyaltyProgramAdmin: RepresentativeAccessTypes(2)
    data object Billing: RepresentativeAccessTypes(4)
    data object RepresentativeAdmin: RepresentativeAccessTypes(8)
    data object OfferAdmin: RepresentativeAccessTypes(16)
    data object ConsumerAdmin: RepresentativeAccessTypes(32)
    data object TeamAdmin: RepresentativeAccessTypes(64)

    companion object: FlagExtraction {
        override val allFlags: List<Flag>
            get() = listOf(
                MerchantAdmin,
                LoyaltyProgramAdmin,
                Billing,
                RepresentativeAdmin,
                OfferAdmin,
                ConsumerAdmin,
                TeamAdmin,
            )
    }
}

sealed class EmployeeAccessTypes(val employeeAccessType: Int): Flag(employeeAccessType) {
    data object DeviceAdmin: EmployeeAccessTypes(1)
    data object EmployeeAdmin: EmployeeAccessTypes(2)
    data object LocationAdmin: EmployeeAccessTypes(4)
    data object OfferAdmin: EmployeeAccessTypes(8)
    data object ConsumerAccess: EmployeeAccessTypes(16)
    data object ConsumerReward: EmployeeAccessTypes(32)
    data object SaleOverview: EmployeeAccessTypes(64)
    data object OrderAdmin: EmployeeAccessTypes(128)
    data object ProductAdmin: EmployeeAccessTypes(256)
    data object LoyaltyProgramAdmin: EmployeeAccessTypes(512)
    data object QuestionAdmin: EmployeeAccessTypes(1024)
    data object BillingAdmin: EmployeeAccessTypes(2048)
    data object SendReceiveMessages: EmployeeAccessTypes(4096)
    data object TabletopAdmin: EmployeeAccessTypes(8192)
    data object ReportAdmin: EmployeeAccessTypes(16384)
    data object PayAtTableFull: EmployeeAccessTypes(32768)
    data object PayAtTablePartial: EmployeeAccessTypes(65536)

    companion object: FlagExtraction {
        override val allFlags: List<Flag>
            get() = listOf(
                DeviceAdmin,
                EmployeeAdmin,
                LocationAdmin,
                OfferAdmin,
                ConsumerAccess,
                ConsumerReward,
                SaleOverview,
                OrderAdmin,
                ProductAdmin,
                LoyaltyProgramAdmin,
                QuestionAdmin,
                BillingAdmin,
                SendReceiveMessages,
                TabletopAdmin,
                ReportAdmin,
                PayAtTableFull,
                PayAtTablePartial,
            )
    }
}

sealed class RepresentativeTypes(val representativeType: Int): Flag(representativeType) {
    data object Admin: RepresentativeTypes(1)
    data object Support: RepresentativeTypes(2)
    data object Sales: RepresentativeTypes(4)
    data object SuperAdmin: RepresentativeTypes(8)

    companion object: FlagExtraction {
        override val allFlags: List<Flag>
            get() = listOf(
                Admin,
                Support,
                Sales,
                SuperAdmin,
            )
    }
}

fun Int?.hasFlag(flag: Flag): Boolean {
    return this?.let { ((this and flag.bitValue) == flag.bitValue) } ?: false
}

fun Int?.hasFlags(vararg flags: Flag): Boolean {
    val hasAllFlags = flags.firstOrNull { flag -> !this.hasFlag(flag) } == null
    return hasAllFlags
}

fun Int?.withFlag(flag: Flag): Int {
    return this.orZero() + flag.bitValue.takeIf { !this.hasFlag(flag) }.orZero()
}

fun Int?.withoutFlag(flag: Flag): Int {
    return this.orZero() - flag.bitValue.takeIf { this.hasFlag(flag) }.orZero()
}

fun Int?.orZero(): Int {
    return this ?: 0
}